import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Gift, Share2, Users, TrendingUp, Award } from "lucide-react";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { useTheme } from "@/lib/theme";

export default function Referrals() {
  const [data, setData] = useState(null);
  const { branding } = useTheme();

  const load = async () => {
    const { data } = await api.get("/referrals/me");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const link = `${window.location.origin}/signup?ref=${data.code}`;
  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  };
  const share = () => {
    if (navigator.share) {
      navigator.share({ title: branding.platform_name, text: `Try ${branding.platform_name} with me — ${branding.tagline}`, url: link });
    } else {
      copy();
    }
  };

  const STATS = [
    { label: "Total referrals", value: data.funnel.total, icon: Users, testid: "ref-stat-total" },
    { label: "Signed up", value: data.funnel.signed_up, icon: TrendingUp, testid: "ref-stat-signed" },
    { label: "Qualified", value: data.funnel.qualified, icon: Gift, testid: "ref-stat-qualified" },
    { label: "Months credited", value: data.months_credit, icon: Award, testid: "ref-stat-credit" },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Rewards"
        title="Refer & earn"
        description="Share your link. Earn 1 month free for every friend who upgrades to a paid plan."
        testid="referrals-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-8 space-y-8">
        <div className="border border-border p-8 bg-foreground text-background relative overflow-hidden" data-testid="referral-link-card">
          <div className="absolute inset-0 diag-stripes opacity-30" />
          <div className="relative">
            <Gift className="w-7 h-7 mb-4" />
            <div className="label-eyebrow opacity-70 mb-2">Your unique link</div>
            <div className="font-mono text-sm bg-background/10 px-3 py-2 mb-4 break-all">{link}</div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={copy} className="rounded-none bg-primary text-primary-foreground" data-testid="referral-copy-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy link
              </Button>
              <Button onClick={share} variant="outline" className="rounded-none border-white/30 bg-transparent hover:bg-white/10 text-background" data-testid="referral-share-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
              <div className="ml-auto text-xs opacity-70 flex items-center">Code: <span className="font-mono ml-2 text-base font-bold">{data.code}</span></div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3">
          {STATS.map((s) => (
            <div key={s.label} className="border border-border p-5" data-testid={s.testid}>
              <div className="flex items-center justify-between mb-2">
                <span className="label-eyebrow">{s.label}</span>
                <s.icon className="w-4 h-4 opacity-60" />
              </div>
              <div className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>{s.value}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-3">
          <div className="border border-border p-6">
            <div className="label-eyebrow mb-4">Recent referrals</div>
            {data.referrals.length === 0 ? (
              <div className="text-sm text-muted-foreground">No referrals yet. Share your link to start earning.</div>
            ) : (
              <ul className="divide-y divide-border">
                {data.referrals.map((r) => (
                  <li key={r.id} className="py-3 flex items-center justify-between" data-testid={`ref-row-${r.id}`}>
                    <div>
                      <div className="font-mono text-xs text-muted-foreground">{r.referred_user_id.slice(0, 8)}…</div>
                      <div className="text-xs text-muted-foreground">{fmtRelative(r.created_at)}</div>
                    </div>
                    <span className={
                      r.status === "rewarded" ? "chip chip-approved" :
                      r.status === "qualified" ? "chip chip-approved" :
                      r.status === "signed_up" ? "chip chip-open" : "chip chip-pending"
                    }>{r.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border border-border p-6">
            <div className="label-eyebrow mb-4">Rewards ledger</div>
            {data.rewards.length === 0 ? (
              <div className="text-sm text-muted-foreground">No rewards yet. They'll appear when a referred user upgrades to a paid plan.</div>
            ) : (
              <ul className="divide-y divide-border">
                {data.rewards.map((r) => (
                  <li key={r.id} className="py-3 flex items-center justify-between" data-testid={`reward-row-${r.id}`}>
                    <div>
                      <div className="font-semibold">+{r.months_credit} month{r.months_credit !== 1 ? "s" : ""} free</div>
                      <div className="text-xs text-muted-foreground">{fmtRelative(r.created_at)}</div>
                    </div>
                    <span className="chip chip-approved">{r.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
